import { useState } from "react";
import { refusalOf } from "./answer";
import { useRenewScreenCode, useRevokeScreenCode, useScreenCode } from "./control";
import { PAIR_PATH } from "./pairCode";
import { shownUrl } from "./screenLinks";
import { Button } from "../ui/Button";
import { Confirm } from "../ui/Confirm";

/**
 * ═══ LE CODE DE L'ÉCRAN DE SALLE, SUR LA RÉGIE ═══
 *
 * Le poste du vidéoprojecteur n'est connecté à rien. On l'ouvrait avec un lien par écran,
 * jeton de trente-deux caractères compris, à taper à la main. Il n'y a plus qu'UN code
 * par événement : on le génère ici, on le revoit à tout moment, on le remplace, on le
 * révoque ; sur le poste, on ouvre `/ecran` et on le tape.
 *
 * ⚠️ REMPLACER ET RÉVOQUER COUPENT LES ÉCRANS OUVERTS, aussitôt : c'est ce qu'on veut
 * quand un code a circulé, et une surprise quand on clique par erreur pendant la séance.
 * D'où la question, qui met la sortie en avant.
 *
 * Displayed strings are French: this is a French product.
 */
export function ScreenCodeManager() {
  const { data, isPending, isError } = useScreenCode();
  const renew = useRenewScreenCode();
  const revoke = useRevokeScreenCode();
  const [asked, setAsked] = useState<"renew" | "revoke" | null>(null);

  const address = shownUrl(`${window.location.origin}${PAIR_PATH}`);
  const refusal = renew.error ?? revoke.error;

  if (isPending) return <p className="empty">Chargement du code.</p>;
  /* « Aucun code » est une affirmation : une lecture ratée ne la fait pas. */
  if (isError || !data) {
    return (
      <p className="live__err" role="alert">
        Le code de l'écran de salle n'a pas pu être lu.
      </p>
    );
  }

  return (
    <div className="scode">
      {data.code ? (
        <>
          <p className="hint">
            Sur le poste de la salle, ouvrez <b>{address}</b> et tapez&nbsp;:
          </p>
          {/* Le code et les deux gestes qui le touchent, sur une ligne : empilés sous
              l'aperçu du participant, ils prenaient la hauteur d'une activité (#452). */}
          <div className="scode__l">
            <p className="scode__code">{data.code}</p>
            <div className="scode__a">
              <Button variant="ghost" loading={renew.isPending} onClick={() => setAsked("renew")}>
                Nouveau code
              </Button>
              <Button variant="ghost" loading={revoke.isPending} onClick={() => setAsked("revoke")}>
                Révoquer
              </Button>
            </div>
          </div>
          <p className="hint">
            Il vaut jusqu'à ce que vous le révoquiez ou le remplaciez, pour autant de
            postes qu'il faut.
          </p>
        </>
      ) : (
        <>
          <p className="hint">
            {data.unreadable
              ? "Le code de cet événement ne peut plus être relu : générez-en un nouveau."
              : "Aucun code pour l'instant. Générez-en un : sur le poste de la salle, il suffira d'ouvrir "}
            {data.unreadable ? null : <b>{address}</b>}
            {data.unreadable ? null : " et de le taper."}
          </p>
          <div className="scode__a">
            <Button loading={renew.isPending} onClick={() => renew.mutate()}>
              Générer un code
            </Button>
          </div>
        </>
      )}

      {refusal ? (
        <p className="live__err" role="alert">
          {refusalOf(refusal)}
        </p>
      ) : null}

      <Confirm
        open={asked === "renew"}
        title="Remplacer le code ?"
        message="Les écrans de salle ouverts avec le code actuel s'éteignent aussitôt : il faudra taper le nouveau sur chaque poste."
        confirmLabel="Nouveau code"
        busy={renew.isPending}
        onConfirm={() => renew.mutate(undefined, { onSettled: () => setAsked(null) })}
        onCancel={() => setAsked(null)}
        emphasis="cancel"
      />
      <Confirm
        open={asked === "revoke"}
        title="Révoquer le code ?"
        message="Les écrans de salle ouverts avec ce code s'éteignent aussitôt, et il n'y aura plus de code tant que vous n'en générez pas un."
        confirmLabel="Révoquer"
        busy={revoke.isPending}
        onConfirm={() => revoke.mutate(undefined, { onSettled: () => setAsked(null) })}
        onCancel={() => setAsked(null)}
        emphasis="cancel"
      />
    </div>
  );
}
